import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

/**
 * A custom element that manages the sticky add-to-cart bar.
 * Shows when user scrolls past the buy buttons on desktop product pages.
 *
 * @extends {Component}
 */
class StickyAddToCart extends Component {
  #ticking = false;
  #lastScrollTop = 0;
  #animationTimeout = null;
  #animationDelay = 150; // Match header animation delay
  #buyButtonsBlock = null;
  #headerObserver = null;
  #intersectionObserver = null;
  #quantityChangeHandlers = new Map();

  get buyButtonsBlock() {
    if (!this.#buyButtonsBlock) {
      const blockId = this.dataset.blockId;
      this.#buyButtonsBlock = document.querySelector(
        `.buy-buttons-block--${blockId}`
      );
    }
    return this.#buyButtonsBlock;
  }

  connectedCallback() {
    super.connectedCallback();

    // Move sticky bar to body level to escape any parent stacking contexts
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
    }

    if (!this.buyButtonsBlock) return;

    // Initialize quantity from main form's quantity selector
    this.#syncQuantityFromMainForm();

    this.#setupEventListeners();
    this.#initIntersectionObserver();
    this.#updateVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  #cleanup() {
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
      this.#animationTimeout = null;
    }
    if (this.#headerObserver) {
      this.#headerObserver.disconnect();
      this.#headerObserver = null;
    }
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
    window.removeEventListener("scroll", this.#requestTick, { passive: true });
    window.removeEventListener("resize", this.#requestTick, { passive: true });
    document.removeEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate
    );

    // Remove quantity change listeners
    this.#quantityChangeHandlers.forEach((handlers, input) => {
      if (handlers.changeHandler) {
        input.removeEventListener("change", handlers.changeHandler);
      }
      if (handlers.inputHandler) {
        input.removeEventListener("input", handlers.inputHandler);
      }
    });
    this.#quantityChangeHandlers.clear();

    // Remove quantity selector update listener
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantitySelectorUpdate
    );
  }

  #initIntersectionObserver() {
    if (!this.buyButtonsBlock) return;
    // Use the actual product-form-buttons element for more accurate detection
    const target =
      this.buyButtonsBlock.querySelector(".product-form-buttons") ||
      this.buyButtonsBlock;
    if (!target) return;

    // Recreate observer on init to apply latest breakpoints/rootMargin
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }

    const isMobile = window.innerWidth < 990;
    // On mobile, appear earlier by shrinking the root bottom (negative bottom margin)
    // e.g. -180px means we treat the viewport as 180px shorter, so we trigger sooner.
    const rootMargin = isMobile ? "0px 0px -180px 0px" : "0px";

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        const scrollTop = document.scrollingElement?.scrollTop ?? 0;
        const isScrollingUp = scrollTop > this.#lastScrollTop;
        this.#lastScrollTop = scrollTop;

        // If buy buttons are intersecting (visible in viewport)
        if (entry.isIntersecting) {
          // Only hide sticky when scrolling up past buy buttons (they come back into view)
          // Don't hide when scrolling down or when buy buttons are already visible
          if (isScrollingUp && this.classList.contains("visible")) {
            this.setAttribute("data-animating", "");
            this.#animationTimeout = setTimeout(() => {
              this.classList.remove("visible");
              this.removeAttribute("data-animating");
            }, this.#animationDelay);
          }
        } else {
          // Buy buttons are out of view - show sticky bar
          this.removeAttribute("data-animating");
          this.classList.add("visible");
        }
      },
      {
        root: null,
        rootMargin,
        threshold: 0.1, // 10% of element must be visible (was incorrectly set to 10)
      }
    );

    this.#intersectionObserver.observe(target);
  }

  #updateVisibility() {
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isMobile = window.innerWidth < 990;

    if (!this.buyButtonsBlock) {
      this.#ticking = false;
      return;
    }

    // Update header position immediately on scroll for seamless transition
    this.#updateHeaderPosition();

    // Check if buy buttons have been scrolled past
    // Use the actual product-form-buttons element for more accurate detection
    const buttonsContainer =
      this.buyButtonsBlock?.querySelector(".product-form-buttons") ||
      this.buyButtonsBlock;
    if (!buttonsContainer) {
      this.#ticking = false;
      return;
    }

    const buyButtonsRect = buttonsContainer.getBoundingClientRect();

    // Determine if buy buttons have been scrolled past
    let isScrolledPast = buyButtonsRect.bottom < 0;

    // Clear any pending timeout
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
      this.#animationTimeout = null;
    }

    if (isScrolledPast) {
      // Show sticky bar when scrolled past buy buttons
      if (isScrollingUp) {
        // Show immediately when scrolling up (user wants to see it)
        this.removeAttribute("data-animating");
        this.classList.add("visible");
      } else if (!this.classList.contains("visible")) {
        // Show when scrolling down past buttons (first time)
        this.removeAttribute("data-animating");
        this.classList.add("visible");
      }
      // Keep visible if already shown and scrolling down
    } else {
      // Hide when buy buttons are visible (scrolled back up)
      if (this.classList.contains("visible")) {
        this.setAttribute("data-animating", "");
        this.#animationTimeout = setTimeout(() => {
          this.classList.remove("visible");
          this.removeAttribute("data-animating");
        }, this.#animationDelay);
      }
    }

    // Set position based on viewport
    // Styling for sticky bar positioning is now handled exclusively via CSS breakpoints,
    // so no JS required here for setting top/bottom.

    this.#lastScrollTop = scrollTop;
    this.#ticking = false;
  }

  #requestTick = () => {
    if (!this.#ticking) {
      window.requestAnimationFrame(() => {
        this.#updateVisibility();
        this.#ticking = false;
      });
      this.#ticking = true;
    }
  };

  #setupEventListeners() {
    window.addEventListener("scroll", this.#requestTick, { passive: true });
    window.addEventListener(
      "resize",
      () => {
        // Re-initialize observer on resize to update rootMargin for breakpoint change
        this.#initIntersectionObserver();
        this.#requestTick();
      },
      { passive: true }
    );

    // Listen for header state changes
    const header = document.querySelector("#header-component");
    if (header) {
      this.#headerObserver = new MutationObserver(() => {
        this.#updateHeaderPosition();

        this.#requestTick();
      });
      this.#headerObserver.observe(header, {
        attributes: true,
        attributeFilter: ["data-sticky-state"],
      });
      // Initial update
      this.#updateHeaderPosition();
    }

    // Listen for variant update events
    document.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate
    );

    // Listen for quantity selector update events (dispatched by quantity-selector-component)
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantitySelectorUpdate
    );

    // Also listen for direct input/change events on quantity inputs
    const quantityInputs = document.querySelectorAll(
      '.quantity-selector input[type="number"]'
    );
    quantityInputs.forEach((input) => {
      const changeHandler = (e) => this.#handleQuantityChange(e);
      const inputHandler = (e) => this.#handleQuantityChange(e);
      input.addEventListener("change", changeHandler);
      input.addEventListener("input", inputHandler);
      this.#quantityChangeHandlers.set(input, { changeHandler, inputHandler });
    });
  }

  #handleVariantUpdate = (event) => {
    if (!event.detail || !event.detail.resource) return;

    const variant = event.detail.resource;
    const productId = event.detail.data?.productId || this.dataset.productId;

    // Only update if it's for this product
    if (productId !== this.dataset.productId) return;

    const variantTitleEl = this.querySelector('[ref="variantTitle"]');
    const quantityTextEl = this.querySelector('[ref="quantityText"]');
    const priceContainerEl = this.querySelector('[ref="priceContainer"]');
    const variantIdInput = this.querySelector('[ref="variantId"]');
    const stickyImageEl = this.querySelector(".sticky-add-to-cart__image img");

    // Update variant ID in form
    if (variantIdInput && variant.id) {
      variantIdInput.value = variant.id;
    }

    // Update variant title
    if (variantTitleEl && variant) {
      if (variant.title && variant.title !== "Default Title") {
        variantTitleEl.textContent = variant.title;
      } else if (event.detail.data?.html) {
        const variantPicker =
          event.detail.data.html.querySelector("variant-picker");
        if (variantPicker) {
          const selectedOptions = variantPicker.querySelectorAll(
            '[data-selected="true"]'
          );
          if (selectedOptions.length > 0) {
            const variantTitle = Array.from(selectedOptions)
              .map((opt) => opt.textContent.trim())
              .join(" / ");
            variantTitleEl.textContent =
              variantTitle || variantTitleEl.textContent;
          }
        }
      }
    }

    // Update price from the updated HTML
    if (priceContainerEl && event.detail.data?.html) {
      const newPriceContainer = event.detail.data.html.querySelector(
        'product-price [ref="priceContainer"]'
      );
      if (newPriceContainer) {
        const priceEl = newPriceContainer.querySelector(".price");
        const comparePriceEl =
          newPriceContainer.querySelector(".compare-at-price");

        if (priceEl) {
          priceContainerEl.innerHTML = "";
          const currentPrice = document.createElement("span");
          currentPrice.className = "sticky-add-to-cart__price-current";
          currentPrice.textContent = priceEl.textContent.trim();
          priceContainerEl.appendChild(currentPrice);

          if (comparePriceEl) {
            const comparePrice = document.createElement("span");
            comparePrice.className = "sticky-add-to-cart__price-compare";
            comparePrice.textContent = comparePriceEl.textContent.trim();
            priceContainerEl.appendChild(comparePrice);
          }
        }
      }
    }

    // Update variant image
    if (variant.featured_media?.preview_image?.src && stickyImageEl) {
      stickyImageEl.src = variant.featured_media.preview_image.src.replace(
        /&width=\d+/,
        "&width=112"
      );
      stickyImageEl.srcset = "";
    }
  };

  #handleQuantitySelectorUpdate = (event) => {
    // Handle QuantitySelectorUpdateEvent from quantity-selector-component
    const quantity = event.detail?.quantity;
    if (quantity !== undefined) {
      this.#updateQuantityDisplay(quantity);
      this.#updateQuantityInput(quantity);
    }
  };

  #handleQuantityChange = (event) => {
    // Handle direct input/change events on quantity inputs
    if (event.target) {
      const quantity = parseInt(event.target.value) || 1;
      this.#updateQuantityDisplay(quantity);
      this.#updateQuantityInput(quantity);
    }
  };

  #updateQuantityDisplay(quantity) {
    const quantityTextEl = this.querySelector('[ref="quantityText"]');
    if (quantityTextEl) {
      quantityTextEl.textContent = `Qty: ${quantity}`;
    }
  }

  #updateQuantityInput(quantity) {
    // Update the hidden quantity input in the sticky form
    const quantityInput = this.querySelector('[ref="quantityInput"]');
    if (quantityInput) {
      quantityInput.value = quantity;
    }
  }

  #syncQuantityFromMainForm() {
    // Get the quantity from the main form's quantity selector
    const mainQuantityInput = this.buyButtonsBlock?.querySelector(
      '.quantity-selector input[type="number"][name="quantity"]'
    );
    if (mainQuantityInput) {
      const quantity = parseInt(mainQuantityInput.value) || 1;
      this.#updateQuantityDisplay(quantity);
      this.#updateQuantityInput(quantity);
    }
  }

  #updateHeaderPosition() {
    // Update sticky bar position based on header visibility
    // This runs on every scroll frame for seamless transitions
    const isMobile = window.innerWidth < 990;
    if (isMobile) {
      // On mobile, sticky bar is positioned at bottom via CSS - don't modify top style
      // Remove any inline top style to let CSS handle positioning
      this.style.removeProperty("top");
      return;
    }

    const header = document.querySelector("#header-component");
    if (!header) return;

    // Check header position in viewport directly for immediate response
    const headerRect = header.getBoundingClientRect();
    const isHeaderVisible = headerRect.top >= -1 && headerRect.bottom > 0;

    // Also check sticky state for additional context
    const stickyState = header.getAttribute("data-sticky-state");
    const isHeaderAnimating = header.hasAttribute("data-animating");

    // If header is above viewport (scrolled past) or animating out, move sticky bar to top
    // Use a small threshold to account for header animation timing
    if (
      !isHeaderVisible ||
      stickyState === "idle" ||
      (stickyState === "active" && isHeaderAnimating)
    ) {
      this.style.top = "0px";
    } else {
      // Header is visible - position sticky bar below it
      // Use header height CSS variable for accurate positioning
      const headerHeight =
        getComputedStyle(document.body).getPropertyValue("--header-height") ||
        "0px";
      this.style.top = `calc(${headerHeight} - 1px)`;
    }
  }
}

if (!customElements.get("sticky-add-to-cart")) {
  customElements.define("sticky-add-to-cart", StickyAddToCart);
}
